import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { RiLock2Line, RiEyeLine, RiEyeOffLine, RiCheckLine, RiCheckboxBlankCircleLine } from "@remixicon/react";
import { Input } from '@components/ui/Input';
import { Label } from '@components/ui/Label';
import { usePasswordValidation } from '@/hooks/usePasswordValidation';

/**
 * Password input component with visibility toggle and validation
 *
 * @param {string} id - Input field ID
 * @param {string} value - Password value
 * @param {function} onChange - Change handler
 * @param {string} placeholder - Input placeholder
 * @param {boolean} required - Whether field is required
 * @param {boolean} disabled - Whether field is disabled
 * @param {string} label - Label text
 * @param {boolean} showLabel - Whether to show label
 * @param {boolean} hasError - Whether field has error
 * @param {boolean} showValidation - Whether to show password validation requirements
 */
const PasswordInput = ({
  id,
  value,
  onChange,
  placeholder = '••••••••',
  required = false,
  disabled = false,
  label = 'Password',
  showLabel = true,
  hasError = false,
  showValidation = false,
}) => {
  const [showPassword, setShowPassword] = useState(false);
  
  // Password validation using custom hook
  const { validation: passwordValidation, isValid: isPasswordValid, strength } = usePasswordValidation(value);

  return (
    <div className="space-y-2">
      {showLabel && (
        <Label htmlFor={id}>
          {label}
          {required && <span className="text-[#0C3E71]"> *</span>}
        </Label>
      )}
      <div className="relative">
        <span className="absolute left-3 top-[23px] transform -translate-y-1/2 text-[#5C5C5C]">
          <RiLock2Line size={15} />
        </span>
        <Input
          id={id}
          type={showPassword ? "text" : "password"}
          placeholder={placeholder}
          value={value}
          onChange={onChange}
          required={required}
          disabled={disabled}
          className={`h-11 !pl-8 !bg-white !border-2 ${hasError ? 'border-red-500' : '!border-border-light'} !text-slate-500 !rounded-lg`}
        />
        <span 
          className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 cursor-pointer" 
          onClick={() => setShowPassword(!showPassword)}
        >
          {showPassword ? <RiEyeLine size={20} /> : <RiEyeOffLine size={20} />}
        </span>
      </div>
      
      {showValidation && (
        <div className="mt-2 space-y-2">
          {/* Strength bar */}
          {value.length > 0 && (
            <div className="flex gap-1">
              {[1, 2, 3, 4].map((level) => (
                <div
                  key={level}
                  className={`h-1 flex-1 rounded-full transition-colors duration-200 ${
                    strength >= level
                      ? strength <= 1
                        ? 'bg-red-500'
                        : strength <= 2
                          ? 'bg-orange-400'
                          : strength <= 3
                            ? 'bg-yellow-400'
                            : 'bg-green-500'
                      : 'bg-gray-200'
                  }`}
                />
              ))}
            </div>
          )}

          {value.length > 0 && (
            <>
              <p className="text-xs text-gray-500">Must contain at least:</p>
              <ul className="space-y-1">
                <li className={`flex items-center gap-2 text-xs transition-colors duration-200 ${
                  passwordValidation.hasUpperCase && passwordValidation.hasLowerCase ? 'text-green-700' : 'text-gray-500'
                }`}>
                  {passwordValidation.hasUpperCase && passwordValidation.hasLowerCase ? (
                    <RiCheckLine className="w-4 h-4 text-green-600 flex-shrink-0" />
                  ) : (
                    <RiCheckboxBlankCircleLine className="w-4 h-4 text-gray-400 flex-shrink-0" />
                  )}
                  <span>At least 1 uppercase and 1 lowercase</span>
                </li>
                <li className={`flex items-center gap-2 text-xs transition-colors duration-200 ${
                  passwordValidation.hasNumber ? 'text-green-700' : 'text-gray-500'
                }`}>
                  {passwordValidation.hasNumber ? (
                    <RiCheckLine className="w-4 h-4 text-green-600 flex-shrink-0" />
                  ) : (
                    <RiCheckboxBlankCircleLine className="w-4 h-4 text-gray-400 flex-shrink-0" />
                  )}
                  <span>At least 1 number</span>
                </li>
                <li className={`flex items-center gap-2 text-xs transition-colors duration-200 ${
                  passwordValidation.minLength ? 'text-green-700' : 'text-gray-500'
                }`}>
                  {passwordValidation.minLength ? (
                    <RiCheckLine className="w-4 h-4 text-green-600 flex-shrink-0" />
                  ) : (
                    <RiCheckboxBlankCircleLine className="w-4 h-4 text-gray-400 flex-shrink-0" />
                  )}
                  <span>At least 12 characters</span>
                </li>
              </ul>
            </>
          )}
        </div>
      )}
    </div>
  );
};

PasswordInput.propTypes = {
  id: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  required: PropTypes.bool,
  disabled: PropTypes.bool,
  label: PropTypes.string,
  showLabel: PropTypes.bool,
  hasError: PropTypes.bool,
  showValidation: PropTypes.bool,
};

export default PasswordInput;
